import { useState, useEffect } from 'react';
import { apiClient } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, UserX, Shield, Home } from 'lucide-react';
import { toast } from 'sonner';
import { useSafeBack } from '../hooks/useSafeBack';
import { resolveAvatarUrl } from '../lib/avatarUrl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BlockedUsers = () => {
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/profile');
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState({});
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [userToUnblock, setUserToUnblock] = useState(null);

  useEffect(() => {
    loadBlockedUsers();
  }, []);

  const loadBlockedUsers = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/blocked-users');
      setBlockedUsers(res.data.blocked_users || []);
    } catch (error) {
      console.error('Failed to load blocked users', error?.response?.data || error);
      toast.error('Failed to load blocked users');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockClick = (user) => {
    setUserToUnblock(user);
    setUnblockDialogOpen(true);
  };

  const handleUnblockConfirm = async () => {
    if (!userToUnblock) return;
    
    const userId = userToUnblock.id;
    setUnblocking(prev => ({ ...prev, [userId]: true }));
    
    try {
      await apiClient.delete(`/block/${userId}`);
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
      toast.success(`Unblocked ${userToUnblock.username}`);
    } catch (error) {
      console.error('Failed to unblock user', error?.response?.data || error);
      toast.error('Failed to unblock user');
    } finally {
      setUnblocking(prev => ({ ...prev, [userId]: false }));
      setUnblockDialogOpen(false);
      setUserToUnblock(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <div
        className="bg-white border-b border-zinc-100 sticky top-0 z-10"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="max-w-lg mx-auto px-4 pb-4 flex items-center gap-3">
          <button
            onClick={goBack}
            className="p-1.5 hover:bg-zinc-100 rounded-full transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-600" />
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="p-1.5 hover:bg-zinc-100 rounded-full transition-colors"
            data-testid="nav-home-btn"
            aria-label="Dashboard"
          >
            <Home className="w-4 h-4 text-zinc-500" />
          </button>
          <h1 className="text-lg font-semibold text-zinc-900">Blocked Users</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Info Banner */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Shield className="w-5 h-5 text-zinc-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-zinc-600">
            <p className="font-medium text-zinc-700 mb-1">About Blocked Users</p>
            <p>Blocked users cannot see your hosted games, follow you, or interact with you. Unblocking will allow them to find and follow you again.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-800"></div>
          </div>
        ) : blockedUsers.length === 0 ? (
          <div className="text-center py-12">
            <UserX className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500 mb-2">No blocked users</p>
            <p className="text-sm text-zinc-400">When you block someone, they'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {blockedUsers.map(user => (
              <div
                key={user.id}
                className="bg-white rounded-lg border border-zinc-200 p-4 flex items-center justify-between"
                data-testid={`blocked-user-${user.id}`}
              >
                <div className="flex items-center gap-3">
                  {user.profile_image_url ? (
                    <img
                      src={resolveAvatarUrl(user.profile_image_url)}
                      alt={user.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center">
                      <span className="text-zinc-500 font-medium">
                        {user.username?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-zinc-900">{user.username}</p>
                    {user.blocked_at && (
                      <p className="text-xs text-zinc-400">
                        Blocked {formatDate(user.blocked_at)}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUnblockClick(user)}
                  disabled={unblocking[user.id]}
                  data-testid={`unblock-btn-${user.id}`}
                  className="text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                >
                  {unblocking[user.id] ? 'Unblocking...' : 'Unblock'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unblock Confirmation Dialog */}
      <AlertDialog open={unblockDialogOpen} onOpenChange={setUnblockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock {userToUnblock?.username}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will allow {userToUnblock?.username} to see your hosted games, follow you, and interact with you again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnblockConfirm}>
              Unblock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BlockedUsers;
